/**
 * Built-in staff role presets — the "quick pick" chips shown on
 * the Add Staff form. Studios can extend the list via the Staff
 * Role Library (the org-scoped `staffRoleLibrary` collection),
 * but every studio starts from these defaults.
 *
 * Mirrored across mobile + web — define once here so the two
 * surfaces always show the same defaults.
 */

export type StaffRoleOption = { key: string; label: string };

/** Built-in presets tuned for an Indian interior-design studio:
 *   - On-site supervision  (Site Incharge, Supervisor, Site Engineer)
 *   - Skilled tradespeople (Carpenter, Electrician, Plumber, etc.)
 *   - Helpers / labour     (Helper)
 *   - Studio / back-office (Designer, Accountant, Office Admin)
 *
 *  Each role is a label string — staff docs store the role as
 *  free-text. */
export const DEFAULT_STAFF_ROLES: StaffRoleOption[] = [
  // On-site supervision
  { key: 'site_incharge', label: 'Site Incharge' },
  { key: 'supervisor', label: 'Supervisor' },
  { key: 'site_engineer', label: 'Site Engineer' },
  // Skilled trades
  { key: 'carpenter', label: 'Carpenter' },
  { key: 'electrician', label: 'Electrician' },
  { key: 'plumber', label: 'Plumber' },
  { key: 'painter', label: 'Painter' },
  { key: 'pop_false_ceiling', label: 'POP / False Ceiling' },
  { key: 'tile_marble_mason', label: 'Tile / Marble Mason' },
  // Helpers
  { key: 'helper', label: 'Helper' },
  // Studio / back-office
  { key: 'designer', label: 'Designer' },
  { key: 'accountant', label: 'Accountant' },
  { key: 'office_admin', label: 'Office Admin' },
];

/** Slugify a free-text role label into a stable key. Used by
 *  the Staff Role Library to dedupe custom additions against
 *  the defaults. */
export function toRoleKey(label: string): string {
  return (
    label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'staff'
  );
}
